import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { LeadSourcesPanel } from "./LeadSourcesPanel";
import { DomainCheckButton } from "@/components/domaincheck/DomainCheckButton";
import { PixelBadge } from "@/components/pixels/PixelBadge";

const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 hover:bg-black hover:text-white";

function ProfileLine({ pr }) {
  const ty = pr.ty_design || {};
  return (
    <div className="text-[10.5px] text-zinc-700" data-testid={`buyer-pages-profile-${pr.code}`}>
      <span className="font-mono">?b={pr.code}</span>
      {pr.is_test && <span className="ml-1 border border-black bg-amber-200 px-1 text-[9px] uppercase">Test</span>}
      {pr.status !== "published" && <span className="ml-1 border border-amber-700 px-1 text-[9px] uppercase text-amber-800">{pr.status}</span>}
      <span className="ml-1 inline-block align-middle"><PixelBadge pageId={pr.id} testId={`buyer-pages-pixel-${pr.code}`} /></span>
      {" · "}{pr.own_pages} own page{pr.own_pages === 1 ? "" : "s"} · {pr.leads_30d} lead{pr.leads_30d === 1 ? "" : "s"} in 30 days
      {" · "}Thank-you: <b data-testid={`buyer-pages-layout-${pr.code}`}>{ty.name || "Classic"}{ty.is_default ? " (site default)" : ""}</b>
    </div>
  );
}

function BuyerCard({ b, open, onToggle, onChanged }) {
  const p = `buyer-pages-buyer-${b.org_id}`;
  const empty = b.has_delivery
    ? "No landing page yet. Use \u201c+ New buyer page\u201d in Custom buyer pages further down and pick their delivery."
    : "No delivery set up yet, so no page can send them leads. Add a delivery on their Manage page first.";
  return (
    <div className="border-2 border-black bg-white" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        {b.profiles.length > 0 ? (
          <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 font-bold" data-testid={`${p}-toggle`}>
            {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />} {b.name}
          </button>
        ) : <b>{b.name}</b>}
        {b.status && b.status !== "active" && <span className="text-[10px] uppercase tracking-widest text-amber-800">{b.status}</span>}
        {b.profiles.length > 0 && <span className="ml-auto"><DomainCheckButton body={{ org_id: b.org_id }} testId={`${p}-domain-check`} /></span>}
        <Link to={`/clients/${b.org_id}/manage`} className={`${BTN} ${b.profiles.length ? "" : "ml-auto"}`} data-testid={`${p}-manage`}>Manage buyer</Link>
        <div className="w-full space-y-0.5">
          {b.profiles.length ? b.profiles.map((pr) => <ProfileLine key={pr.id} pr={pr} />)
            : <div className="text-[10.5px] text-zinc-500" data-testid={`${p}-empty`}>{empty}</div>}
        </div>
      </div>
      {open && <div className="border-t-2 border-black bg-zinc-50 p-3"><LeadSourcesPanel orgId={b.org_id} onChanged={onChanged} /></div>}
    </div>
  );
}

// Landing Pages → Buyer pages: each buyer's landing pages, their traffic and leads, and the thank-you page visitors see.
export function BuyerPagesTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const load = () => api.get("/admin/buyer-pages-overview").then((r) => setData(r.data)).catch(() => setData({ failed: true, buyers: [] }));
  useEffect(() => { load(); }, []);
  const needle = q.trim().toLowerCase();
  const rows = (data?.buyers || []).filter((b) => !needle || `${b.name} ${b.profiles.map((x) => x.code).join(" ")}`.toLowerCase().includes(needle));
  return (
    <div className="space-y-3 p-4" data-testid="buyer-pages-tab">
      <p className="max-w-3xl text-xs text-zinc-700">
        Every buyer, the landing pages that send them traffic and leads, and the thank-you page their visitors see.
        Open a buyer to preview a page or its thank-you page, switch the thank-you layout, edit a page or add one.
      </p>
      <label className="flex max-w-sm items-center gap-2 border-2 border-black bg-white px-2 py-1">
        <MagnifyingGlass size={13} weight="bold" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a buyer or buyer code" className="w-full text-sm outline-none" data-testid="buyer-pages-search" />
      </label>
      {!data && <div className="label-mono" data-testid="buyer-pages-loading">Loading…</div>}
      {data?.failed && <div className="text-xs text-rose-700" data-testid="buyer-pages-error">Couldn&apos;t load buyers. Refresh to try again.</div>}
      {data && !data.failed && !rows.length && <div className="label-mono" data-testid="buyer-pages-empty">{needle ? "No buyer matches that search." : "No buyers yet."}</div>}
      {rows.map((b) => <BuyerCard key={b.org_id} b={b} open={Boolean(open[b.org_id])} onToggle={() => setOpen((s) => ({ ...s, [b.org_id]: !s[b.org_id] }))} onChanged={load} />)}
    </div>
  );
}
